/**
 * Activities API Service
 * Handles activities, restaurants, and accommodations endpoints
 */

import { getMockActivities, getMockRestaurants } from '@/services/utils/mocks.js';

export class ActivitiesService {
  constructor(apiClient, errorHandler) {
    this.apiClient = apiClient;
    this.handleApiError = errorHandler;
  }

  /**
   * Get activities/attractions for a destination
   * @param {string} destination - Destination name
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of activities
   */
  async getActivities(destination, options = {}) {
    try {
      const params = {
        travelers: options.travelers || 2,
        max_price: options.maxPrice,
        min_rating: options.minRating,
        categories: options.categories,
        limit: options.limit || 20
      };
      const response = await this.apiClient.get(`/activities/recommendations/${encodeURIComponent(destination)}`, { params });
      if (response.data && response.data.activities) {
        return response.data.activities.map(activity => ({
          id: activity.place_id || activity.name,
          place_id: activity.place_id,
          name: activity.name,
          category: activity.category,
          rating: activity.rating,
          duration: activity.duration_hours ? `${activity.duration_hours} hours` : 'Various',
          price: activity.price,
          currency: activity.currency,
          description: activity.description,
          image_url: activity.image_url || 'https://images.unsplash.com/photo-1551632811-561732d1e306?ixlib=rb-4.0.3&w=300&h=300&fit=crop',
          booking_link: activity.booking_link,
          distance_km: activity.distance_km
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error getting activities:', error);
      // Fallback to mock data if API fails
      console.log('Falling back to mock activities data');
      return getMockActivities(destination);
    }
  }

  /**
   * Get restaurants for a destination
   * @param {string} destination - Destination name
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of restaurants
   */
  async getRestaurants(destination, options = {}) {
    try {
      // Map known options to API params; avoid leaking camelCase aliases
      const params = {
        location: destination,
        daily_food_budget: options.budget || 50,
        travelers: options.travelers || 2,
        cuisine_preferences: options.cuisine ? [options.cuisine] : undefined,
        max_price_level: options.maxPrice,
        min_rating: options.minRating,
        radius: options.radius,
        limit: options.limit
      };
      const response = await this.apiClient.get(`/restaurants/recommendations/${encodeURIComponent(destination)}`, { params });
      
      // Check if we got meaningful results
      const restaurants = response.data?.by_budget?.mid_range || response.data?.restaurants || [];
      if (restaurants && restaurants.length > 0) {
        return restaurants;
      }
      
      // If no restaurants found, try search endpoint
      console.log('No restaurants from recommendations endpoint, trying search...');
      return await this.searchRestaurants(destination, options);
      
    } catch (error) {
      console.error('Error getting restaurants:', error);
      
      // Fallback to mock data like activities does
      console.log('Restaurant API failed, using mock data for:', destination);
      return getMockRestaurants(destination);
    }
  }

  /**
   * Search restaurants with filters
   * @param {string} location - Location to search
   * @param {Object} filters - Search filters
   * @returns {Promise<Array>} Array of restaurants
   */
  async searchRestaurants(location, filters = {}) {
    try {
      const {
        radius = 5000,
        cuisine,
        maxPrice,
        minRating = 3.0,
        limit = 20,
        ...extra
      } = filters;
      const params = {
        location,
        radius,
        cuisine_preferences: cuisine ? [cuisine] : undefined,
        max_price_level: maxPrice,
        min_rating: minRating,
        limit,
        // Forward any additional filters (e.g., customFilter)
        ...extra
      };
      const response = await this.apiClient.get('/restaurants/search', { params });
      // Support both `{ data: [...] }` and raw array responses in tests/backends
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.data)) return response.data;
      return response.data || [];
    } catch (error) {
      console.error('Error searching restaurants:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Get accommodations for a destination
   * @param {string} destination - Destination name
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of accommodations
   */
  async getAccommodations(destination, options = {}) {
    try {
      const params = {
        location: destination,
        guests: options.guests || 2,
        limit: options.limit || 10,
        ...(options.checkIn && { check_in_date: options.checkIn }),
        ...(options.checkOut && { check_out_date: options.checkOut }),
        ...(options.maxPrice != null && { max_price_per_night: options.maxPrice }),
        ...(options.minRating != null && { min_rating: options.minRating }),
      };

      const response = await this.apiClient.get('/accommodations/search', { params });
      return response.data?.accommodations || [];
    } catch (error) {
      console.error('Error getting accommodations:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Search activities with filters
   * @param {string} location - Location to search
   * @param {Object} filters - Search filters
   * @returns {Promise<Array>} Array of activities
   */
  async searchActivities(location, filters = {}) {
    try {
      const {
        category,
        maxPrice,
        minRating = 3.0,
        limit = 20,
        ...extra
      } = filters;
      const params = {
        location,
        category,
        max_price: maxPrice,
        min_rating: minRating,
        limit,
        // Forward any additional filters (e.g., season)
        ...extra
      };
      const response = await this.apiClient.get('/activities/search', { params });
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.data)) return response.data;
      return response.data || [];
    } catch (error) {
      console.error('Error searching activities:', error);
      throw this.handleApiError(error);
    }
  }
}

export default ActivitiesService;